import {
  BrowserRouter,
  Route,
  Routes,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import HomePage from "./pages/HomePage";
import BlogPage from "./pages/BlogPage";
import BlogDetailsPage from "./pages/BlogDetailsPage";
import ContactPage from "./pages/ContactPage";
import NotFoundPage from "./pages/NotFoundPage";
import MasterLayout from "./layout/MasterLayout";
import RouteScrollToTop from "./helper/RouteScrollToTop";
import ScrollToTop from "react-scroll-to-top";
import useModals from "./helper/useModals";
import PaymentPage from "./pages/PaymentPage";
import { LocaleWrapper } from "./locales/localeWrapper";
import Privacy from "./pages/Privacy";
import TermsAndConditions from "./pages/TermsAndConditions";
import Profile from "./components/ClientProfile";
import PartnershipPage from "./pages/PartnershipPage";
import PartnerDashboard from "./pages/PartnerDashboard";

function ProtectedProfile() {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  return isAuthenticated ? <Profile /> : <Navigate to="/" replace />;
}

// A small component just to handle redirect on root "/"
function LanguageRedirect() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname === "/") {
      navigate(`/${i18n.language}`, { replace: true });
    }
  }, [pathname, i18n.language, navigate]);

  return null;
}

function App() {
  const modals = useModals();

  return (
    <BrowserRouter>
      {/* <LanguageRedirect /> */}
      <RouteScrollToTop />
      <ScrollToTop color="#A847F0" />
      <LocaleWrapper>
        <Routes>
          <Route path="/" element={<MasterLayout {...modals} />}>

            <Route index element={<HomePage {...modals} />} />
            <Route path="blog" element={<BlogPage {...modals} />} />
            <Route path="blog/:slug" element={<BlogDetailsPage />} />
            <Route path="contact" element={<ContactPage {...modals} />} />
            <Route path="partnership" element={<PartnershipPage {...modals} />} />
            <Route path="profile" element={<ProtectedProfile />} />
            <Route path="payment" element={<PaymentPage {...modals} />} />
            <Route path="payment/pay" element={<PaymentPage status="pay" />} />
            <Route path="/q/:rowId" element={<PaymentPage status="pay" />} />
            <Route
              path="payment/failed"
              element={<PaymentPage status="failed" />}
            />
            <Route
              path="payment/success"
              element={<PaymentPage status="success" />}
            />
            <Route
              path="payment/pending"
              element={<PaymentPage status="pending" />}
            />
            <Route path="privacy-policy" element={<Privacy />} />
            <Route
              path="terms-and-conditions"
              element={<TermsAndConditions />}
            />

            {/* Other locales */}
            <Route path=":lang">
              <Route index element={<HomePage {...modals} />} />
              <Route path="blog" element={<BlogPage {...modals} />} />
              <Route path="blog/:slug" element={<BlogDetailsPage />} />
              <Route path="contact" element={<ContactPage {...modals} />} />
              <Route path="partnership" element={<PartnershipPage {...modals} />} />
              <Route path="profile" element={<ProtectedProfile />} />
              <Route path="payment" element={<PaymentPage {...modals} />} />
              <Route
                path="payment/pay"
                element={<PaymentPage status="pay" />}
              />
              <Route
                path="payment/failed"
                element={<PaymentPage status="failed" />}
              />
              <Route
                path="payment/success"
                element={<PaymentPage status="success" />}
              />
              <Route
                path="payment/pending"
                element={<PaymentPage status="pending" />}
              />
              <Route path="privacy-policy" element={<Privacy />} />
              <Route
                path="terms-and-conditions"
                element={<TermsAndConditions />}
              />
            </Route>
          </Route>

          {/* Partner is intentionally outside MasterLayout: no public nav and no footer. */}
          <Route path="partner/:page" element={<PartnerDashboard />} />
          <Route path=":lang/partner/:page" element={<PartnerDashboard />} />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </LocaleWrapper>
    </BrowserRouter>
  );
}

export default App;
